import React, { useCallback, useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { notifications } from '@mantine/notifications'
import {
  createAllLocationsPricingRow,
  deleteAllLocationsPricingRow,
  fetchAllLocationsPricingRows,
} from '../../data/admin.service'
import { AllLocationsPricingRow } from '../../models/Admin/AllLocationsPricingRow'
import { formatCurrency, getAllLocationsDurationLabelVerbose } from '../../utils/displayUtils'

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const AllLocationsPricingTab: React.FC = () => {
  const { instance } = useMsal()
  const [rows, setRows] = useState<AllLocationsPricingRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  // Form state
  const [newNumberOfDays, setNewNumberOfDays] = useState<number | string>('')
  const [newPrice, setNewPrice] = useState<number | string>('')
  const [newEffectiveDate, setNewEffectiveDate] = useState<Date | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const loadRows = useCallback(async () => {
    try {
      const data = await fetchAllLocationsPricingRows(instance)
      setRows(data)
    } catch (error) {
      console.error('Error fetching all-locations pricing:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to load pricing rows. Please try again.',
        color: 'red',
        autoClose: true,
      })
    } finally {
      setIsLoading(false)
    }
  }, [instance])

  useEffect(() => {
    loadRows()
  }, [loadRows])

  const handleOpenCreateModal = () => {
    setNewNumberOfDays('')
    setNewPrice('')
    setNewEffectiveDate(null)
    setCreateError(null)
    setCreateModalOpen(true)
  }

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false)
    setCreateError(null)
  }

  const handleCreate = async () => {
    const days = typeof newNumberOfDays === 'number' ? newNumberOfDays : parseInt(String(newNumberOfDays), 10)
    const price = typeof newPrice === 'number' ? newPrice : parseFloat(String(newPrice))

    if (!days || isNaN(days) || days <= 0) {
      setCreateError('Number of days must be a positive integer.')
      return
    }
    if (!price || isNaN(price) || price <= 0) {
      setCreateError('Price must be a positive number.')
      return
    }
    if (!newEffectiveDate) {
      setCreateError('Effective date is required.')
      return
    }

    setIsCreating(true)
    setCreateError(null)
    try {
      const created = await createAllLocationsPricingRow(
        instance,
        {
          numberOfDays: days,
          price,
          effectiveDate: newEffectiveDate.toISOString(),
        },
      )
      setRows((prev) => [created, ...prev])
      setCreateModalOpen(false)
      notifications.show({
        title: 'Success',
        message: `Pricing row for ${getAllLocationsDurationLabelVerbose(created.numberOfDays)} created successfully.`,
        color: 'green',
        autoClose: true,
      })
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to create pricing row.'
      setCreateError(message)
    } finally {
      setIsCreating(false)
    }
  }

  const handleConfirmDelete = async (id: number) => {
    setDeletingId(id)
    setConfirmDeleteId(null)
    try {
      await deleteAllLocationsPricingRow(id, instance)
      setRows((prev) => prev.filter((r) => r.id !== id))
      notifications.show({
        title: 'Success',
        message: 'Pricing row deleted.',
        color: 'green',
        autoClose: true,
      })
    } catch (error) {
      console.error('Error deleting pricing row:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to delete pricing row. Please try again.',
        color: 'red',
        autoClose: true,
      })
    } finally {
      setDeletingId(null)
    }
  }

  if (isLoading) {
    return (
      <Box py={24}>
        <Group justify='center' mt={50}>
          <Loader size='lg' />
        </Group>
      </Box>
    )
  }

  return (
    <Box py={24}>
      <Group justify='space-between' mb={24}>
        <Title>All Locations Pricing</Title>
        <Button onClick={handleOpenCreateModal}>Add Price</Button>
      </Group>

      {rows.length === 0 ? (
        <Paper withBorder p={36} mt={32}>
          <Text ta='center'>No pricing rows yet. Add one above.</Text>
        </Paper>
      ) : (
        <Paper withBorder radius={16} p={0} style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Price</Table.Th>
                <Table.Th>Effective Date</Table.Th>
                <Table.Th>Created Date</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text fw={600}>{getAllLocationsDurationLabelVerbose(row.numberOfDays)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text>{formatCurrency(row.price)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{formatDate(row.effectiveDate)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{formatDate(row.createdDate)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size='xs'
                      color='red'
                      variant='outline'
                      disabled={deletingId === row.id}
                      loading={deletingId === row.id}
                      onClick={() => setConfirmDeleteId(row.id)}
                    >
                      Delete
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Create modal */}
      <Modal
        opened={createModalOpen}
        onClose={handleCloseCreateModal}
        title='Add Pricing Row'
        centered
      >
        <Stack gap='md'>
          <NumberInput
            label='Number of Days'
            placeholder='e.g. 30'
            value={newNumberOfDays}
            onChange={setNewNumberOfDays}
            min={1}
            required
          />
          <NumberInput
            label='Price (USD)'
            placeholder='e.g. 99.99'
            value={newPrice}
            onChange={setNewPrice}
            min={0.01}
            decimalScale={2}
            step={0.01}
            required
          />
          <DatePickerInput
            label='Effective Date'
            placeholder='Pick a date'
            value={newEffectiveDate}
            onChange={setNewEffectiveDate}
            required
          />
          {createError && (
            <Text c='red' size='sm'>
              {createError}
            </Text>
          )}
          <Group justify='flex-end' mt='sm'>
            <Button variant='default' onClick={handleCloseCreateModal} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={isCreating}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Confirm delete modal */}
      <Modal
        opened={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title='Confirm Delete'
        centered
        size='sm'
      >
        <Stack gap='md'>
          <Text>Are you sure you want to delete this pricing row? This cannot be undone.</Text>
          <Group justify='flex-end'>
            <Button variant='default' onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              color='red'
              loading={deletingId !== null}
              onClick={() => confirmDeleteId !== null && handleConfirmDelete(confirmDeleteId)}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}

export default AllLocationsPricingTab
